import axios, {
  AxiosInstance,
  InternalAxiosRequestConfig,
  AxiosResponse,
} from 'axios';
import { sessionService } from '@/shared/services/session.service';
import { getApiBaseUrl } from '@/shared/config/config';

// Configuración base de la API
const API_BASE_URL = getApiBaseUrl();
const TENANT_NOT_FOUND_REASON = 'tenant-not-found';
const ONBOARDING_REQUIRED_REASON = 'onboarding-required';

const extractErrorMessage = (error: any): string => {
  const payload = error?.response?.data;
  if (!payload) return '';

  if (typeof payload === 'string') {
    return payload;
  }

  if (Array.isArray(payload?.message)) {
    return payload.message.join(' ');
  }

  if (typeof payload?.message === 'string') {
    return payload.message;
  }

  if (typeof payload?.error === 'string') {
    return payload.error;
  }

  return '';
};

const isTenantNotFoundError = (error: any): boolean => {
  if (!error?.response) return false;

  const candidateMessage = extractErrorMessage(error).toLowerCase();

  if (!candidateMessage) return false;

  return (
    candidateMessage.includes('tenant no encontrado') ||
    candidateMessage.includes('tenant not found') ||
    candidateMessage.includes('tenant fue eliminado')
  );
};

/**
 * Endpoints de lectura no-crítica: un 401 acá NO debe cerrar sesión ni redirigir.
 * Espejo de SILENT_401_ENDPOINTS en shared/utils/axios-config.ts — la lista vivía
 * sólo en aquella instancia y los callers de `adminApi`/`privateApi` quedaban fuera.
 */
const SILENT_401_ENDPOINTS = [
  '/notifications',
  '/plans/limits',
  '/plans/stats',
  '/subscriptions/trial-days',
  '/ai/consent',
  '/multi-site',
  '/sites',
];

const isSilent401Endpoint = (url?: string): boolean =>
  !!url && SILENT_401_ENDPOINTS.some(ep => url.includes(ep));

/**
 * Rutas donde el usuario legítimamente NO tiene sesión todavía. Expulsarlo desde
 * acá es un bucle: ya está donde lo mandaríamos. `/login` ya se contemplaba;
 * faltaban las de alta — por eso un anónimo en /register terminaba en /login.
 */
const PUBLIC_AUTH_ROUTES = ['/login', '/register', '/signup', '/forgot-password', '/reset-password'];

const isPublicAuthRoute = (): boolean =>
  PUBLIC_AUTH_ROUTES.some(r => window.location.pathname.includes(r));

const forceLogoutAndRedirect = (reason?: string) => {
  sessionService.clearSession();
  sessionService.clearSession(true);

  const isLoginPage = window.location.pathname.includes('/login');

  if (!isLoginPage) {
    const query = reason ? `?reason=${reason}` : '';
    window.location.href = `/login${query}`;
  }
};

const needsOnboardingRedirect = (error: any): boolean => {
  if (!error?.response) return false;

  const status = error.response.status;
  if (status !== 403) return false;

  const candidateMessage = extractErrorMessage(error).toLowerCase();
  if (!candidateMessage) return false;

  const isAccessDenied =
    candidateMessage.includes('acceso denegado') ||
    candidateMessage.includes('roles requeridos') ||
    candidateMessage.includes('onboarding');

  if (!isAccessDenied) return false;

  const user = sessionService.getUser();

  if (!user) return true;

  const pending =
    !user.role ||
    user.role === 'PENDING_ONBOARDING' ||
    !user.onboardingCompleted;

  return pending;
};

const redirectToOnboarding = () => {
  if (!window.location.pathname.includes('/onboarding')) {
    window.location.href = `/onboarding?reason=${ONBOARDING_REQUIRED_REASON}`;
  }
};

// Instancia pública (sin token)
export const publicApi: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Instancia privada (con token)
export const privateApi: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Instancia para SUPER_ADMIN (puede especificar tenant dinámicamente)
export const adminApi: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json',
  },
});

/**
 * GH#1859 — rutas que el api sirve SIN credenciales (app.config.ts:61-69: refleja el
 * Origin con `credentials: false`, a propósito, para los sitios white-label).
 * Si el navegador manda cookies a estas rutas, el preflight falla («Allow-Credentials
 * must be true») y la llamada no sale: así quedaron mudos la caja negra de crashes
 * (/public/client-errors), la página pública de links de pago y el listado público.
 * Espejo del criterio del servidor: si uno cambia, el otro también.
 */
const RUTAS_SIN_COOKIES = ['/public/', '/buyer/'];

export const esRutaSinCookies = (url?: string): boolean => {
  if (!url) return false;
  // Acepta relativa ('/public/x'), con prefijo ('/api/v1/public/x') o absoluta.
  const path = url.replace(/^https?:\/\/[^/]+/, '').split('?')[0].replace(/^\/api\/v1(?=\/)/, '');
  return RUTAS_SIN_COOKIES.some(p => path.startsWith(p));
};

// Interceptor para requests públicos
publicApi.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    // Credenciales para que las cookies se puedan establecer (login, register…),
    // salvo en las rutas que el api sirve sin credenciales (GH#1859). Va `false`
    // EXPLÍCITO: axios.defaults.withCredentials es true en toda la app.
    config.withCredentials = !esRutaSinCookies(config.url);
    return config;
  },
  error => {
    return Promise.reject(error);
  }
);

// Interceptor para responses públicos
publicApi.interceptors.response.use(
  (response: AxiosResponse) => {
    return response;
  },
  error => {
    // Manejar errores globales para requests públicos
    if (error.response?.status === 401) {
      // Redirigir al login si es necesario
    }
    return Promise.reject(error);
  }
);

// Interceptor para requests privados (Authorization Bearer)
// x-tenant-id header REMOVIDO 2026-05-31 backend autoritativo es JWT.tenantId TenantScopeInterceptor
privateApi.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    // Keep withCredentials for same-domain cookie fallback (local dev)
    config.withCredentials = true;

    // Send Authorization Bearer header (cross-domain compatible)
    const token = sessionService.getToken();
    if (token && config.headers) {
      config.headers['Authorization'] = `Bearer ${token}`;
    }

    // No sobreescribir Content-Type si es FormData - axios lo maneja automáticamente
    if (config.data instanceof FormData && config.headers) {
      delete config.headers['Content-Type'];
    }

    // x-tenant-id header REMOVIDO 2026-05-31 backend autoritativo es JWT.tenantId TenantScopeInterceptor
    // Backend deriva tenantId 100% del JWT, ya no acepta header cliente-controlled.

    return config;
  },
  error => {
    return Promise.reject(error);
  }
);

// Interceptor para responses privados
privateApi.interceptors.response.use(
  (response: AxiosResponse) => {
    return response;
  },
  error => {
    if (needsOnboardingRedirect(error)) {
      redirectToOnboarding();
      return Promise.reject(error);
    }

    if (isTenantNotFoundError(error)) {
      forceLogoutAndRedirect(TENANT_NOT_FOUND_REASON);
      return Promise.reject(error);
    }

    // Manejar errores globales para requests privados
    if (error.response?.status === 401) {
      // Solo limpiar sesión si no estamos en una ruta pública de auth Y no es una
      // petición de verificación de token. `isPublicAuthRoute` reemplaza al viejo
      // chequeo de sólo `/login`: /register y /signup también son sitios donde el
      // visitante legítimamente todavía no tiene sesión.
      const isTokenVerification = error.config?.url?.includes('/auth/me') ||
                                 error.config?.url?.includes('/auth/verify');

      if (!isPublicAuthRoute() && !isTokenVerification && !isSilent401Endpoint(error.config?.url)) {
        sessionService.clearSession();
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

// Limpia la sesión local gestionada desde el frontend
export const clearAuthTokens = () => {
  sessionService.clearSession();
};

// Interceptor para requests de admin (SUPER_ADMIN)
// x-tenant-id header REMOVIDO 2026-05-31 backend autoritativo es JWT.tenantId TenantScopeInterceptor
adminApi.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    // Keep withCredentials for same-domain cookie fallback (local dev)
    config.withCredentials = true;

    // Send Authorization Bearer header (cross-domain compatible)
    const token = sessionService.getToken();
    if (token && config.headers) {
      config.headers['Authorization'] = `Bearer ${token}`;
    }

    // x-tenant-id header REMOVIDO 2026-05-31 backend autoritativo es JWT.tenantId TenantScopeInterceptor
    // Backend deriva tenantId 100% del JWT, ya no acepta header cliente-controlled.
    // Para SUPER_ADMIN cross-tenant operations, usar query param o path param explicito.

    return config;
  },
  error => {
    return Promise.reject(error);
  }
);

// Interceptor para responses de admin
adminApi.interceptors.response.use(
  (response: AxiosResponse) => {
    return response;
  },
  error => {
    if (needsOnboardingRedirect(error)) {
      redirectToOnboarding();
      return Promise.reject(error);
    }

    if (isTenantNotFoundError(error)) {
      forceLogoutAndRedirect(TENANT_NOT_FOUND_REASON);
      return Promise.reject(error);
    }

    // Manejar errores globales para requests de admin
    if (error.response?.status === 401) {
      // Un 401 en un endpoint no-crítico NO debe expulsar a nadie.
      //
      // La misma protección existe desde 2026-06-06 en el axios global
      // (shared/utils/axios-config.ts, SILENT_401_ENDPOINTS) pero se aplicó
      // sólo a ESA instancia; los callers que usan `adminApi` nunca pasaron
      // por ella. Efecto medido en producción (2026-08-08): /plans/limits
      // está en la lista silenciosa y aun así expulsaba, porque quien lo
      // llama es `adminApi`. El arreglo estaba escrito, pero no en el camino
      // que decide.
      if (!isSilent401Endpoint(error.config?.url)) {
        // Solo limpiar sesión si no estamos en rutas de login
        if (!isPublicAuthRoute()) {
          sessionService.clearSession();
          window.location.href = '/login';
        }
      }
    }
    return Promise.reject(error);
  }
);

// Función helper para crear requests con tenant específico (para SUPER_ADMIN)
// x-tenant-id header REMOVIDO 2026-05-31 backend autoritativo es JWT.tenantId TenantScopeInterceptor
// DEPRECATED: el header x-tenant-id ya no es aceptado por backend.
// Para SUPER_ADMIN cross-tenant: pasar tenantId via query param (?tenantId=xxx) o path (/tenants/:tenantId/...).
// Wrapper conservado por compat (no inyecta header); migrar callers a query/path params.
export const createAdminRequestWithTenant = (_tenantId: string) => {
  return {
    get: (url: string, config: any = {}) => adminApi.get(url, config),
    post: (url: string, data: any, config: any = {}) =>
      adminApi.post(url, data, config),
    put: (url: string, data: any, config: any = {}) =>
      adminApi.put(url, data, config),
    delete: (url: string, config: any = {}) => adminApi.delete(url, config),
  };
};

export default {
  publicApi,
  privateApi,
  adminApi,
  clearAuthTokens,
  createAdminRequestWithTenant,
};
